import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { operationType, selectDataType } from "../../../../../services/types";
import { AuthenticateSelect } from "../../../../auth/select";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type AirtableProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

// Operation options
const getOperationOptions = (): selectDataType[] => {
    return [
        { id: 0, name: 'Let AI Agent Decide', refId: 'agent_decide' },
        { id: 1, name: 'Create a new Record', refId: 'create', isActive: true },
        { id: 2, name: 'Get all Records', refId: 'read_list' },
        { id: 3, name: 'Get Record Details', refId: 'read_single' },
        { id: 4, name: 'Update Record', refId: 'update' },
        { id: 5, name: 'Delete Record', refId: 'delete' },
    ];
};

export function Airtable({ id, data, setChildMinimize, setChildSettings }: AirtableProps) {
    const [operations, setOperations] = React.useState<selectDataType[]>(getOperationOptions());
    const [bases, setBases] = React.useState<selectDataType[]>([]);
    const [tables, setTables] = React.useState<selectDataType[]>([]);
    const [records, setRecords] = React.useState<selectDataType[]>([]);
    const [tableFields, setTableFields] = React.useState<{ id: string; name: string; type: string }[]>([]);

    const { setNodes } = useReactFlow();
    const conn = data.value?.connection;
    // const credential = conn ? `personalAccessToken=${encodeURIComponent(conn.personalAccessToken)}` : '';
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isLoadingBases, setIsLoadingBases] = React.useState(false);
    const [isLoadingTables, setIsLoadingTables] = React.useState(false);

    // --
    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Initialize operation
    React.useEffect(() => {
        if (!data.value) {
            updateNodeValue(id, setNodes, { operation: 'create' });
        } else {
            const currentOperation = data.value.operation || 'create';
            setOperations(ops => ops.map(o => ({ ...o, isActive: o.refId === currentOperation })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch bases whenever a connection is present but metadata hasn't been loaded yet.
    // Not gated on isExpanded: a connection can be assigned from the Global Connections
    // popup while the node is collapsed, and it must fully initialize immediately —
    // the same as if the user had opened the node and picked the connection manually.
    React.useEffect(() => {
        if (conn?.personalAccessToken && bases.length === 0) {
            fetchBases(conn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conn]);

    // Handle connection authentication
    const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
        if (type === 'add') {
            updateNodeValue(id, setNodes, { connection: connectionData });
            // Fetch bases after connection
            await fetchBases(connectionData);
        } else if (type === 'remove') {
            updateNodeValue(id, setNodes, { connection: null, bases: [], tables: [], records: [], fields: {} });
            setBases([]);
            setTables([]);
            setRecords([]);
        }
    };

    // Fetch available bases
    const fetchBases = async (connData?: any) => {
        const c = connData || conn;
        if (!c?.personalAccessToken) return;

        setIsLoadingBases(true);
        const cred = `personalAccessToken=${encodeURIComponent(c.personalAccessToken)}`;

        try {
            const result = (await axios.get(`${webURL}/crm/airtable/bases?${cred}`)).data;
            const basesList = result.map((item: any, index: number) => ({
                id: index + 1,
                name: item.name,
                refId: item.id,
                isActive: data.value?.baseId === item.id || index === 0
            }));
            setBases(basesList);

            // Auto-select first base if none selected
            if (basesList.length > 0 && !data.value?.baseId) {
                const firstBase = basesList[0];
                updateNodeValue(id, setNodes, { baseId: firstBase.refId });
                await fetchTables(firstBase.refId, c);
            } else if (data.value?.baseId) {
                await fetchTables(data.value.baseId, c);
            }
        } catch (err) {
            console.error('Failed to fetch bases:', err);
            setBases([]);
        }
        setIsLoadingBases(false);
    };

    // Fetch tables for selected base
    const fetchTables = async (baseId: string, connData?: any) => {
        const c = connData || conn;
        if (!c?.personalAccessToken || !baseId) return;

        setIsLoadingTables(true);
        const cred = `personalAccessToken=${encodeURIComponent(c.personalAccessToken)}`;

        try {
            const result = (await axios.get(`${webURL}/crm/airtable/${baseId}/tables?${cred}`)).data;
            const tablesList = result.map((item: any, index: number) => ({
                id: index + 1,
                name: item.name,
                refId: item.id,
                fields: item.fields,
                isActive: data.value?.tableId === item.id || index === 0
            }));
            setTables(tablesList);

            // Auto-select first table and set fields
            if (tablesList.length > 0) {
                const selectedTable = tablesList.find((t: any) => t.isActive) || tablesList[0];
                if (!data.value?.tableId) {
                    updateNodeValue(id, setNodes, { tableId: selectedTable.refId });
                }
                setTableFields(selectedTable.fields || []);

                // Fetch records for update/delete operations
                const currentOp = data.value?.operation || 'create';
                if (['read_single', 'update', 'delete'].includes(currentOp)) {
                    await fetchRecords(baseId, selectedTable.refId, c);
                }
            }
        } catch (err) {
            console.error('Failed to fetch tables:', err);
            setTables([]);
        }
        setIsLoadingTables(false);
    };

    // Fetch records for the current table
    const fetchRecords = async (baseId?: string, tableId?: string, connData?: any) => {
        const c = connData || conn;
        const bId = baseId || data.value?.baseId;
        const tId = tableId || data.value?.tableId;
        if (!c?.personalAccessToken || !bId || !tId) return;

        const cred = `personalAccessToken=${encodeURIComponent(c.personalAccessToken)}`;

        try {
            const result = (await axios.get(`${webURL}/crm/airtable/${bId}/${tId}?${cred}`)).data;
            const recordsList = result.map((item: any, index: number) => {
                // Try to get a meaningful name from common field names
                const fields = item.fields || {};
                const displayName = fields.Name || fields.name || fields.Title || fields.title ||
                    fields['Lead Name'] || fields['Lead name'] || fields['lead_name'] ||
                    fields.Email || fields.email ||
                    // Fallback: get first string value from fields
                    Object.values(fields).find(v => typeof v === 'string' && v.length > 0) ||
                    item.id;

                return {
                    id: index + 1,
                    name: String(displayName).substring(0, 50), // Limit length for display
                    refId: item.id,
                    fields: item.fields,
                    isActive: index === 0
                };
            });
            setRecords(recordsList);
            updateNodeValue(id, setNodes, { records: recordsList });
        } catch (err) {
            console.error('Failed to fetch records:', err);
            setRecords([]);
        }
    };

    // Handle input changes
    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string, isFields?: boolean) => {
        if (keyRef) {
            if (isFields) {
                setNodes(nodes => nodes.map(node => {
                    if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, fields: { ...node.data.value.fields, [keyRef]: e.target.value } } } }
                    else return node;
                }));
            } else {
                updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
            }
        }
    };

    // Handle base selection
    const handleSelectBase = async (selectId: number) => {
        const selected = bases.find(b => b.id === selectId);
        if (!selected) return;

        setBases(bases => bases.map(b => ({ ...b, isActive: b.id === selectId })));
        updateNodeValue(id, setNodes, { baseId: selected.refId, tableId: null, records: [], fields: {} });
        setTables([]);
        setRecords([]);

        await fetchTables(selected.refId as string);
    };

    // Handle table selection
    const handleSelectTable = async (selectId: number) => {
        const selected = tables.find(t => t.id === selectId);
        if (!selected) return;

        setTables(tables => tables.map(t => ({ ...t, isActive: t.id === selectId })));
        updateNodeValue(id, setNodes, { tableId: selected.refId, records: [], fields: {} });
        setTableFields((selected as any).fields || []);
        setRecords([]);

        // Fetch records for update/delete operations
        const currentOp = data.value?.operation || 'create';
        if (['read_single', 'update', 'delete'].includes(currentOp)) {
            await fetchRecords(data.value?.baseId, selected.refId as string);
        }
    };

    // Handle operation selection
    const handleSelectOperation = async (selectId: number) => {
        const refId = operations.find(o => o.id === selectId)?.refId || 'create';
        setOperations(operations => operations.map(o => ({ ...o, isActive: o.id === selectId })));
        updateNodeValue(id, setNodes, { operation: refId });

        // Refresh records for read/update/delete operations
        if (['read_single', 'update', 'delete'].includes(refId) && conn && data.value?.baseId && data.value?.tableId) {
            await fetchRecords();
        }
    };

    // Handle record selection
    const handleSelectRecord = async (selectId: number) => {
        const selected = records.find(r => r.id === selectId);
        if (!selected) return;

        setRecords(recs => recs.map(r => ({ ...r, isActive: r.id === selectId })));

        // Populate fields with record data for update (only editable fields)
        if (data.value?.operation === 'update' && (selected as any).fields) {
            // Filter out read-only fields to avoid sending them on update
            const readOnlyTypes = [
                'autoNumber', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy',
                'formula', 'rollup', 'count', 'lookup', 'button', 'externalSyncSource', 'aiText',
                'multipleAttachments', 'barcode', 'multipleLookupValues', 'multipleRecordLinks'
            ];
            const editableFieldNames = tableFields
                .filter(f => !readOnlyTypes.includes(f.type))
                .map(f => f.name);

            const editableFields: Record<string, any> = {};
            for (const [key, value] of Object.entries((selected as any).fields)) {
                if (editableFieldNames.includes(key)) {
                    editableFields[key] = value;
                }
            }

            updateNodeValue(id, setNodes, {
                records: records.map(r => ({ ...r, isActive: r.id === selectId })),
                fields: editableFields
            });
        } else {
            updateNodeValue(id, setNodes, { records: records.map(r => ({ ...r, isActive: r.id === selectId })) });
        }
    };

    // Computed values
    const currentOperation = data.value?.operation || 'create';
    const isAgentDecide = currentOperation === 'agent_decide';
    const isWarning = !data.value?.connection;
    const showRecordSelector = !isAgentDecide && ['read_single', 'update', 'delete'].includes(currentOperation);
    const showFormFields = !isAgentDecide && ['create', 'update'].includes(currentOperation);

    // Render dynamic form fields based on table schema
    const renderFormFields = () => {
        if (tableFields.length === 0) {
            return null
        }

        // Filter fields - skip internal/computed field types and those that need special formats
        const readOnlyTypes = [
            'autoNumber', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy',
            'formula', 'rollup', 'count', 'lookup', 'button', 'externalSyncSource',
            'aiText', // AI-generated fields like "AI Lead Summary"
            'multipleAttachments', // Attachments require object format, not text
            'barcode', // Barcode needs special format
            'multipleLookupValues', // Lookup values are computed
            'multipleRecordLinks' // Record links need special handling
        ];
        const editableFields = tableFields.filter(f => !readOnlyTypes.includes(f.type));

        return (
            <div className="scroll nowheel">
                {editableFields.slice(0, 10).map((field) => (
                    <TextInput
                        key={field.id}
                        keyRef={field.name}
                        title={field.name}
                        placeholder={`Enter ${field.name}`}
                        value={data.value?.fields?.[field.name] || ''}
                        handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)}
                    />
                ))}
                {editableFields.length > 10 && (
                    <p style={{ color: '#888', fontSize: '0.75rem', padding: '5px 0' }}>
                        +{editableFields.length - 10} more fields (use dynamic input)
                    </p>
                )}
            </div>
        );
    };

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ width: '420px' }}
            ><Styled>
                    <AuthenticateSelect type="airtable" defaultId={data.value?.connection?.id} handleAuthenticate={handleAuthenticate} background={data.background} withLine />

                    {/* Base Select */}
                    <Select
                        title={isLoadingBases ? "Loading Bases..." : "Base"}
                        data={bases}
                        handleSelectItem={(selectId) => handleSelectBase(selectId)}
                        isSmall={true}
                        emptyMsg="Connect to load bases"
                    />

                    {/* Table Select */}
                    <Select
                        title={isLoadingTables ? "Loading Tables..." : "Table"}
                        data={tables}
                        handleSelectItem={(selectId) => handleSelectTable(selectId)}
                        isSmall={true}
                        emptyMsg="Select a base first"
                    />

                    {/* Operation Select */}
                    <Select title="Operation" data={operations} handleSelectItem={(selectId) => handleSelectOperation(selectId)} isSmall={true} />



                    {/* Operation-specific config - hidden when AI decides */}
                    {!isAgentDecide && (<>
                        {/* Record Selector (for read_single, update, delete) */}
                        {showRecordSelector && (
                            <Select
                                title="Record"
                                data={records}
                                handleSelectItem={(selectId) => handleSelectRecord(selectId)}
                                isSmall={true}
                                emptyMsg="No records found"
                            />
                        )}

                        {/* Filter by Formula (for read_list) */}
                        {currentOperation === 'read_list' && (
                            <TextInput
                                keyRef="filterByFormula"
                                title="Filter"
                                placeholder="{Status} = 'Active'"
                                value={data.value?.filterByFormula || ''}
                                handleChange={(e, keyRef) => handleChangeInput(e, keyRef)}
                            />
                        )}

                        {/* Form Fields (for create, update) */}
                        {showFormFields && renderFormFields()}
                    </>)}

                </Styled></ExpandedBlock> : null}
        </Main>
    </>);
}